<script lang="ts">
  import Icon from '../components/Icon.svelte';
  import { onMount } from 'svelte';
  import TopBar from '../components/TopBar.svelte';
  import UpsellBlock from '../components/UpsellBlock.svelte';
  import VideoPlayer from '../components/player/VideoPlayer.svelte';
  import type { PlayerSettings } from '../lib/player/settings';
  import { api, ApiError } from '../lib/api';
  import { openExternalLink } from '../lib/telegram';
  import { attachEventRecordingTracking } from '../lib/playback';
  import type { EventDetailDTO } from '../lib/types';

  interface Props {
    params?: { id?: string };
  }
  let { params }: Props = $props();

  let event = $state<EventDetailDTO | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let joining = $state(false);

  let playbackUrl = $state<string | null>(null);
  // Direct HLS source for our own player (absent → Bunny iframe fallback).
  let hls = $state<{ url: string; poster: string | null; startAt: number; player?: PlayerSettings } | null>(
    null,
  );
  let iframeEl = $state<HTMLIFrameElement | undefined>(undefined);
  let detach: (() => void) | null = null;

  const isPast = $derived(!!event && new Date(event.startsAt).getTime() < Date.now());

  const MONTH_GEN = [
    'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
    'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
  ];
  function whenLabel(iso: string): string {
    const d = new Date(iso);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getDate()} ${MONTH_GEN[d.getMonth()]} ${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  async function load(): Promise<void> {
    if (!params?.id) return;
    loading = true;
    error = null;
    try {
      const ev = await api.event(params.id);
      event = ev;
      if (ev.hasAccess && ev.hasRecording && new Date(ev.startsAt).getTime() < Date.now()) {
        try {
          const r = await api.eventRecordingPlayback(params.id);
          playbackUrl = r.iframeUrl;
          if (r.hlsUrl) hls = { url: r.hlsUrl, poster: r.posterUrl ?? null, startAt: r.startAt ?? 0, player: r.player };
        } catch {
          /* recording not playable — show note */
        }
      }
    } catch (err) {
      error = err instanceof ApiError && err.status === 404 ? 'Подію не знайдено' : 'Не вдалось завантажити';
    } finally {
      loading = false;
    }
  }

  async function join(): Promise<void> {
    if (!params?.id || joining) return;
    joining = true;
    error = null;
    try {
      const { url } = await api.eventJoin(params.id);
      openExternalLink(url);
    } catch {
      error = 'Не вдалось відкрити посилання';
    } finally {
      joining = false;
    }
  }

  function saveRecordingProgress(sec: number, duration: number): void {
    if (!params?.id) return;
    api.eventRecordingProgressSave(params.id, sec, duration).catch(() => {});
  }

  // Iframe fallback only: attach recording progress tracking once the iframe + URL are ready.
  $effect(() => {
    if (!iframeEl || !playbackUrl || !params?.id) return;
    const id = params.id;
    const handleLoad = (): void => {
      detach?.();
      detach = attachEventRecordingTracking(iframeEl!, id);
    };
    iframeEl.addEventListener('load', handleLoad);
    return () => {
      iframeEl?.removeEventListener('load', handleLoad);
      detach?.();
      detach = null;
    };
  });

  onMount(load);
</script>

<div class="screen">
  <TopBar title="Подія" backHref="/schedule" />
  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else if error}
      <div class="placeholder err">{error}</div>
    {:else if event}
      <h1 class="ev-title">{event.title}</h1>
      <div class="ev-when"><Icon name="calendar-days" size="xs" /> {whenLabel(event.startsAt)}{#if event.durationMin} · {event.durationMin} хв{/if}</div>
      {#if event.description}<div class="ev-desc">{event.description}</div>{/if}

      {#if !event.hasAccess}
        <UpsellBlock
          title="Подія доступна за підпискою"
          text="Щоб приєднатись до трансляції та переглядати записи, активуйте підписку на відповідний продукт."
        />
      {:else if isPast}
        {#if event.hasRecording && hls}
          <VideoPlayer
            hlsUrl={hls.url}
            posterUrl={hls.poster}
            startAt={hls.startAt}
            title={event.title}
            settings={hls.player}
            onProgress={saveRecordingProgress}
          />
        {:else if event.hasRecording && playbackUrl}
          <div class="player-wrap">
            <iframe
              bind:this={iframeEl}
              src={playbackUrl}
              title={event.title}
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowfullscreen
            ></iframe>
          </div>
        {:else if event.hasRecording}
          <div class="placeholder">Завантажуємо запис…</div>
        {:else}
          <div class="note">Зустріч завершилась. Запис зʼявиться пізніше.</div>
        {/if}
      {:else if event.streamUrl}
        <button class="join-btn" disabled={joining} onclick={join}>
          {joining ? 'Відкриваємо…' : 'Приєднатись до трансляції'}
        </button>
      {:else}
        <div class="note">Посилання на трансляцію зʼявиться ближче до події.</div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .placeholder {
    padding: 30px 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    border: 1px dashed var(--border2); border-radius: 12px; background: var(--bg2);
  }
  .placeholder.err { color: var(--status-inactive); }
  .ev-title { font-size: 20px; font-weight: 700; color: var(--text); margin: 0 0 8px; line-height: 1.3; }
  .ev-when { font-size: 13px; color: var(--text-muted); margin-bottom: 14px; }
  .ev-desc { font-size: 14px; color: var(--text); line-height: 1.6; white-space: pre-wrap; margin-bottom: 18px; }
  .note {
    padding: 14px; background: var(--bg2); border: 1px solid var(--border); border-radius: 12px;
    font-size: 13px; color: var(--text-muted); line-height: 1.5;
  }
  .join-btn {
    width: 100%; padding: 15px; border: none; border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3)); color: var(--text-light);
    font-size: 15px; font-weight: 700; cursor: pointer; font-family: inherit;
  }
  .join-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .player-wrap { position: relative; width: 100%; aspect-ratio: 16/9; border-radius: 12px; overflow: hidden; background: #000; }
  .player-wrap iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: 0; }
</style>
